/**
 * App Configuration - Central configuration with dynamic domain support
 *
 * The domain is determined dynamically from:
 * 1. Environment variable (VITE_APP_DOMAIN) for build-time override
 * 2. window.location.host for runtime detection
 *
 * This supports:
 * - Current domain: ba.teknology.eu
 * - Future custom domains
 * - Multi-tenant symbolic links (companyname.domain.ext)
 */

// =============================================================================
// DOMAIN DETECTION
// =============================================================================

/**
 * Get the current app domain dynamically
 *
 * Priority:
 * 1. VITE_APP_DOMAIN environment variable (if set)
 * 2. window.location.host (runtime detection)
 * 3. Fallback to 'ba.teknology.eu'
 */
export function getAppDomain(): string {
  // Check for build-time override
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_DOMAIN) {
    return import.meta.env.VITE_APP_DOMAIN;
  }

  // Runtime detection from browser
  if (typeof window !== 'undefined' && window.location?.host) {
    return window.location.host;
  }

  // Fallback for server-side or tests
  return 'ba.teknology.eu';
}

/**
 * Get the protocol (http or https)
 */
export function getProtocol(): string {
  if (typeof window !== 'undefined' && window.location?.protocol) {
    return window.location.protocol.replace(':', '');
  }
  return 'https';
}

/**
 * Get the base URL for the current app
 */
export function getBaseUrl(): string {
  const protocol = getProtocol();
  const domain = getAppDomain();
  return `${protocol}://${domain}`;
}

// =============================================================================
// API ENDPOINTS
// =============================================================================

/**
 * API endpoint configuration
 * All endpoints are relative to the app domain for production,
 * or proxied through Vite in development
 */
export interface ApiEndpoints {
  /** GraphQL API endpoint (main database backend) */
  graphql: string;
  /** AI GraphQL API endpoint (api-server for M.E.A. and AI operations) */
  aiGraphql: string;
  /** File upload endpoint */
  upload: string;
  /** IMAP/SMTP bridge API (via gateway) */
  imap: string;
  /** Whisper transcription server (via gateway) */
  whisper: string;
  /** Groq LLM API (via gateway) */
  groq: string;
  /** Google OAuth (via gateway) */
  google: string;
  /** AI Memory service (via gateway) */
  memory: string;
  /** LiveKit / video calls (via gateway) */
  livekit: string;
  /** Store / file service (via gateway) */
  store: string;
  /** OpenAI API (via gateway) */
  openai: string;
  /** Anthropic API (via gateway) */
  anthropic: string;
}

/**
 * Get API endpoints based on environment
 *
 * In development: Uses Vite proxy (relative URLs)
 * In production: Uses full URLs or proxied through Apache
 */
export function getApiEndpoints(): ApiEndpoints {
  const isDev = typeof import.meta !== 'undefined' &&
    import.meta.env?.DEV === true;

  // Gateway base URL — all services go through the backend gateway
  const gatewayBase = isDev ? '' : 'https://database.teknology.eu';

  if (isDev) {
    // Development: Vite proxies /api/* and /graphql to backend gateway
    return {
      graphql: '/graphql',
      aiGraphql: '/api/ai/graphql',
      upload: '/upload',
      imap: `${gatewayBase}/api/imap`,
      whisper: `${gatewayBase}/api/whisper`,
      groq: `${gatewayBase}/api/groq`,
      google: `${gatewayBase}/api/google`,
      memory: `${gatewayBase}/api/memory`,
      livekit: `${gatewayBase}/api/livekit`,
      store: `${gatewayBase}/api/store`,
      openai: `${gatewayBase}/api/openai`,
      anthropic: `${gatewayBase}/api/anthropic`,
    };
  }

  // Production: All services through backend gateway
  return {
    graphql: `${gatewayBase}/graphql`,
    aiGraphql: '/api/ai/graphql',
    upload: `${gatewayBase}/upload`,
    imap: `${gatewayBase}/api/imap`,
    whisper: `${gatewayBase}/api/whisper`,
    groq: `${gatewayBase}/api/groq`,
    google: `${gatewayBase}/api/google`,
    memory: `${gatewayBase}/api/memory`,
    livekit: `${gatewayBase}/api/livekit`,
    store: `${gatewayBase}/api/store`,
    openai: `${gatewayBase}/api/openai`,
    anthropic: `${gatewayBase}/api/anthropic`,
  };
}

// =============================================================================
// SERVICE CONFIGURATION
// =============================================================================

export interface ServiceConfig {
  /** Backend GraphQL server (Rust) */
  backend: {
    url: string;
    token: string;
  };
  /** IMAP/SMTP email bridge */
  email: {
    apiUrl: string;
    enabled: boolean;
  };
  /** Whisper transcription */
  whisper: {
    url: string;
    enabled: boolean;
  };
}

/**
 * Get service configuration
 */
export function getServiceConfig(): ServiceConfig {
  const endpoints = getApiEndpoints();

  return {
    backend: {
      url: endpoints.graphql,
      token: import.meta.env?.VITE_GRAPHQL_TOKEN || '',
    },
    email: {
      apiUrl: endpoints.imap,
      enabled: true,
    },
    whisper: {
      url: endpoints.whisper,
      enabled: true,
    },
  };
}

// =============================================================================
// TENANT DETECTION (FUTURE: MULTI-TENANT SUPPORT)
// =============================================================================

/**
 * Extract tenant identifier from domain
 *
 * Supports patterns like:
 * - companyname.ba.teknology.eu → tenant: companyname
 * - company.customdomain.com → tenant: company
 * - ba.teknology.eu → tenant: null (main app)
 *
 * @returns Tenant identifier or null for main app
 */
export function getTenantFromDomain(): string | null {
  const domain = getAppDomain();

  // Main domains (not tenant-specific)
  const mainDomains = [
    'ba.teknology.eu',
    'www.ba.teknology.eu',
    'localhost',
  ];

  if (mainDomains.some(main => domain === main || domain.startsWith('localhost'))) {
    return null;
  }

  // Extract subdomain as tenant
  // e.g., companyname.ba.teknology.eu → companyname
  const parts = domain.split('.');
  if (parts.length >= 3) {
    // Check if it's a subdomain of our main domain
    const baseDomain = parts.slice(-2).join('.');
    if (baseDomain === 'teknology.eu') {
      return parts[0];
    }
  }

  // For custom domains, use the first part
  // e.g., company.customdomain.com → company
  if (parts.length >= 2) {
    return parts[0];
  }

  return null;
}

// =============================================================================
// EXPORTS
// =============================================================================

export const appConfig = {
  getDomain: getAppDomain,
  getProtocol,
  getBaseUrl,
  getApiEndpoints,
  getServiceConfig,
  getTenant: getTenantFromDomain,
};

export default appConfig;
